import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Utility function to merge Tailwind CSS classes with clsx
 * Handles conditional classes and removes conflicts
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format file size in bytes to human readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  );
}

/**
 * Debounce function to limit the rate of function calls
 */
export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Throttle function to limit function calls to once per specified time
 */
export function throttle<T extends (...args: unknown[]) => void>(
  func: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

const MAX_UINT32 = 2 ** 32;

const getCrypto = (): Crypto | undefined => {
  if (typeof globalThis === "undefined") return undefined;
  if (!globalThis.crypto?.getRandomValues) return undefined;
  return globalThis.crypto;
};

const randomUint32 = (): number => {
  const cryptoObject = getCrypto();
  if (!cryptoObject) {
    throw new Error("Crypto API is not available");
  }
  const array = new Uint32Array(1);
  cryptoObject.getRandomValues(array);
  return array[0];
};

const secureRandomInt = (min: number, max: number): number => {
  const minValue = Math.ceil(min);
  const maxValue = Math.floor(max);
  if (maxValue < minValue) {
    throw new Error("Invalid random range");
  }
  const range = maxValue - minValue + 1;
  const limit = Math.floor(MAX_UINT32 / range) * range;
  let value = randomUint32();
  while (value >= limit) {
    value = randomUint32();
  }
  return minValue + (value % range);
};

const generateSecureString = (length: number): string => {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    const index = secureRandomInt(0, alphabet.length - 1);
    result += alphabet[index];
  }
  return result;
};

/**
 * Generate a random ID string
 */
export function generateId(length: number = 8): string {
  const cryptoObject =
    typeof globalThis === "undefined" ? undefined : globalThis.crypto;
  if (cryptoObject?.randomUUID) {
    // eslint-disable-next-line @typescript-eslint/prefer-string-replace-all
    return cryptoObject.randomUUID().replace(/-/g, "").slice(0, length);
  }
  if (cryptoObject?.getRandomValues) {
    return generateSecureString(length);
  }
  throw new Error("Crypto API is not available");
}

/**
 * Format date to locale string with options
 */
export function formatDate(
  date: Date | string | number,
  options?: Intl.DateTimeFormatOptions,
): string {
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString("vi-VN", {
    year: "numeric",
    month: "long",
    day: "numeric",
    ...options,
  });
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}
